import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

interface estadosAccesibilidad {
    tamanoFuente: number;
    espaciado: number;
    interlineado: number;
    resaltoEnlaces: boolean;
    altoContraste: boolean;
    ocultarImagenes: boolean;
    pausarAnimaciones: boolean;
    dislexia: boolean;
    granCursor: boolean;
    mascaraLectura: boolean;
    guiaLectura: boolean;
    lenguajeSenas: boolean;

    isDirty: boolean;
    timestamp?: number;
}

const DEFAULT_STATE: estadosAccesibilidad = {
    tamanoFuente: 1,
    espaciado: 0,
    interlineado: 1.5,
    resaltoEnlaces: false,
    altoContraste: false,
    ocultarImagenes: false,
    dislexia: false,
    pausarAnimaciones: false,
    granCursor: false,
    mascaraLectura: false,
    guiaLectura: false,
    lenguajeSenas: false,
    isDirty: false,
};

const STORAGE_KEY = 'a11y-user-config';
const DEBOUNCE_TIME = 100; //ms
const FONT_SIZE_LIMITS = { MIN: 0.8, MAX: 2.0, STEP: 0.1 };
const CSS_CLASSES = {
    HIGH_CONTRAST: 'a11y-alto-contraste',
    HIDE_IMAGES: 'a11y-ocultar-imagenes',
    PAUSE_ANIMATIONS: 'a11y-pausa-animaciones',
    HIGHLIGHT_LINKS: 'a11y-resaltar-enlaces',
    DYSLEXIA_FONT: 'a11y-dyslexia-fuente',
    LARGE_CURSOR: 'a11y-gran-cursor',
    READING_MASK: 'a11y-mascara-lectura',
    READING_GUIDE: 'a11y-guia-lectura',
    TEXT_LARGE: 'a11y-texto-grande',
};

const TEXT_SCALE_THRESHOLD = 1.3; 

//Evitar llamado saturado a funciones de cambio de estilo
class DebounceManager {
    private timeoutId: number | null = null;
    private callbacks: Array<() => void> = [];

    constructor(private wait: number = DEBOUNCE_TIME) {}

    execute(callback: () => void): void {
        this.callbacks.push(callback);
        if (this.timeoutId !== null) {
            clearTimeout(this.timeoutId);
        }

        this.timeoutId = window.setTimeout(() => {
            this.callbacks.forEach((cb) => {
                try {
                    cb();
                } catch (error) {
                    console.error('Error en callback debouncido:', error);
                }
            });

            // Limpiar
            this.callbacks = [];
            this.timeoutId = null;
        }, this.wait);
    }

    cancel(): void {
        if (this.timeoutId !== null) {
            clearTimeout(this.timeoutId);
            this.timeoutId = null;
        }
        this.callbacks = [];
    }
}

//Manejo del local storage
class LocalStorageManager {
    private esDisponible: boolean;

    constructor() {
        this.esDisponible = this.verificarDisponibilidad();
    }

    // Guarda la configuración en localStorage de forma testeada
    private verificarDisponibilidad(): boolean {
        try {
            const test = '__a11y_test__';
            localStorage.setItem(test, test);
            localStorage.removeItem(test);
            return true;
        } catch {
            console.warn('LocalStorage no disponible - cambios no se guardarán');
            return false;
        }
    }

    save(key: string, value: any): boolean {
        if (!this.esDisponible) return false;
        try {
            localStorage.setItem(key, JSON.stringify(value));
            return true;
        } catch (error) {
            if (error instanceof DOMException && error.name === 'QuotaExceededError') {
                console.warn('LocalStorage lleno - no se pudo guardar configuración');
            } else {
                console.error('Error al guardar en localStorage:', error);
            }
            return false;
        }
    }

    load<T>(key: string): T | null {
        if (!this.esDisponible) return null;
        try {
            const item = localStorage.getItem(key);
            return item ? (JSON.parse(item) as T) : null;
        } catch (error) {
            console.error('Error al cargar desde localStorage:', error);
            return null;
        }
    }

    remove(key: string): void {
        if (!this.esDisponible) return;
        try {
            localStorage.removeItem(key);
        } catch (error) {
            console.error('Error al eliminar desde localStorage:', error);
        }
    }
}

class StyleManager {
    private root = document.documentElement;

    /**
     * Aplicar variables CSS para escalas de fuente y espaciado
     */
    applyVariables(state: Partial<estadosAccesibilidad>): void {
        const root = this.root;

        // Solo actualizar si hay cambios
        if (state.tamanoFuente !== undefined) {
            root.style.setProperty('--a11y-tamano-texto', String(state.tamanoFuente));
            this.toggleClass(CSS_CLASSES.TEXT_LARGE, state.tamanoFuente >= TEXT_SCALE_THRESHOLD);
        }

        if (state.espaciado !== undefined) {
            root.style.setProperty('--a11y-espaciado', `${state.espaciado}em`);
        }

        if (state.interlineado !== undefined) {
            root.style.setProperty('--a11y-interlineado', String(state.interlineado));
        }
    }

    /**
     * Toggle de clases CSS (más eficiente que múltiples estilos)
     */
    toggleClass(className: string, enabled: boolean): void {
        this.root.classList.toggle(className, enabled);
    }

    /**
     * Aplicar múltiples clases de una vez (más eficiente)
     */
    applyClasses(state: Partial<estadosAccesibilidad>): void {
        this.toggleClass(CSS_CLASSES.HIGH_CONTRAST, state.altoContraste || false);
        this.toggleClass(CSS_CLASSES.HIDE_IMAGES, state.ocultarImagenes || false);
        this.toggleClass(CSS_CLASSES.PAUSE_ANIMATIONS, state.pausarAnimaciones || false);
        this.toggleClass(CSS_CLASSES.HIGHLIGHT_LINKS, state.resaltoEnlaces || false);
        this.toggleClass(CSS_CLASSES.DYSLEXIA_FONT, state.dislexia || false);
        this.toggleClass(CSS_CLASSES.LARGE_CURSOR, state.granCursor || false);
        this.toggleClass(CSS_CLASSES.READING_MASK, state.mascaraLectura || false);
        this.toggleClass(CSS_CLASSES.READING_GUIDE, state.guiaLectura || false);
    }

    /**
     * Resetear todos los estilos al original
     */
    reset(): void {
        this.root.style.removeProperty('--a11y-tamano-texto');
        this.root.style.removeProperty('--a11y-espaciado');
        this.root.style.removeProperty('--a11y-interlineado');

        Object.values(CSS_CLASSES).forEach((className) => {
            this.root.classList.remove(className);
        });
    }
}

@Injectable({
    providedIn: 'root',
})
export class AccesilityService {
    private state: estadosAccesibilidad = { ...DEFAULT_STATE };
    private debounceManager = new DebounceManager();
    private storageManager = new LocalStorageManager();
    private styleManager = new StyleManager();
    private signLanguageSubject = new BehaviorSubject<boolean>(this.state.lenguajeSenas);
    signLanguage$ = this.signLanguageSubject.asObservable();
    readingGuideEl: HTMLElement | null = null;

    config = JSON.parse(localStorage.getItem('opcionesAccesibilidad') || '{}');

    constructor() {
        this.loadState();
    }

    private applyChanges() {
        this.debounceManager.execute(() => {
            this.styleManager.applyVariables(this.state);
            this.styleManager.applyClasses(this.state);
            this.state.isDirty = false;
        });
    }

    private handleMouseMove = (e: MouseEvent) => {
        document.documentElement.style.setProperty('--mouse-y', `${e.clientY}px`);
    };

    private createReadingGuide = () => {
        if (this.readingGuideEl) return;

        this.readingGuideEl = document.createElement('div');
        this.readingGuideEl.className = 'a11y-reading-guide';

        document.body.appendChild(this.readingGuideEl);
    };

    private removeReadingGuide = () => {
        if (this.readingGuideEl) {
            this.readingGuideEl.remove();
            this.readingGuideEl = null;
        }
    };

    private handleReadingGuideMove = (e: MouseEvent) => {
        if (!this.readingGuideEl) return;
        this.readingGuideEl.style.left = `${e.clientX}px`;
        this.readingGuideEl.style.top = `${e.clientY}px`;
    };

    //Validar tamaño de la letra
    private clampFontSize = (value: number): number => {
        return Math.round(Math.max(FONT_SIZE_LIMITS.MIN, Math.min(FONT_SIZE_LIMITS.MAX, value)) * 100) / 100;
    };

    private saveState() {
        const configToSave = {
            tamanoFuente: this.state.tamanoFuente,
            espaciado: this.state.espaciado,
            interlineado: this.state.interlineado,
            resaltoEnlaces: this.state.resaltoEnlaces,
            altoContraste: this.state.altoContraste,
            ocultarImagenes: this.state.ocultarImagenes,
            dislexia: this.state.dislexia,
            pausarAnimaciones: this.state.pausarAnimaciones,
            granCursor: this.state.granCursor,
            mascaraLectura: this.state.mascaraLectura,
            guiaLectura: this.state.guiaLectura,
            lenguajeSenas: this.state.lenguajeSenas,
            timestamp: Date.now(),
        };

        return this.storageManager.save(STORAGE_KEY, configToSave);
    }

    private loadState() {
        const saved = this.storageManager.load<Partial<estadosAccesibilidad>>(STORAGE_KEY);

        if (saved) {
            Object.assign(this.state, saved);
            this.state.isDirty = true;
            this.styleManager.applyVariables(this.state);
            this.styleManager.applyClasses(this.state);
            this.state.isDirty = false;

            if (this.state.mascaraLectura) {
                window.addEventListener('mousemove', this.handleMouseMove);
            }
            if (this.state.guiaLectura) {
                this.createReadingGuide();
                window.addEventListener('mousemove', this.handleReadingGuideMove);
            }

            this.signLanguageSubject.next(this.state.lenguajeSenas);
            return true;
        }

        return false;
    }

    resetState() {
        Object.assign(this.state, DEFAULT_STATE);
        this.state.isDirty = true;
        this.styleManager.reset();
        window.removeEventListener('mousemove', this.handleMouseMove);
        window.removeEventListener('mousemove', this.handleReadingGuideMove);
        this.removeReadingGuide();
        this.storageManager.remove(STORAGE_KEY);
        this.state.isDirty = false;
    }

    cleanup = (): void => {
        window.removeEventListener('mousemove', this.handleMouseMove);
        window.removeEventListener('mousemove', this.handleReadingGuideMove);
        this.debounceManager.cancel();
    };

    incrementFontSize = (): boolean => {
        const newSize = this.clampFontSize(this.state.tamanoFuente + FONT_SIZE_LIMITS.STEP);

        if (newSize !== this.state.tamanoFuente) {
            this.state.tamanoFuente = newSize;
            this.state.isDirty = true;
            this.applyChanges();
            this.saveState();
            return true;
        }
        return false;
    };

    decrementFontSize = (): boolean => {
        const newSize = this.clampFontSize(this.state.tamanoFuente - FONT_SIZE_LIMITS.STEP);

        if (newSize !== this.state.tamanoFuente) {
            this.state.tamanoFuente = newSize;
            this.state.isDirty = true;
            this.applyChanges();
            this.saveState();
            return true;
        }
        return false;
    };

    toggleSpacing() {
        const newSpacing = this.state.espaciado === 0 ? 0.05 : 0;
        const newLineHeight = newSpacing > 0 ? 1.8 : 1.5;

        this.state.espaciado = newSpacing;
        this.state.interlineado = newLineHeight;
        this.state.isDirty = true;
        this.applyChanges();
        this.saveState();
    }

    toggleLinks() {
        this.state.resaltoEnlaces = !this.state.resaltoEnlaces;
        this.state.isDirty = true;
        this.styleManager.toggleClass(CSS_CLASSES.HIGHLIGHT_LINKS, this.state.resaltoEnlaces);
        this.saveState();
    }

    toggleContrast() {
        this.state.altoContraste = !this.state.altoContraste;
        this.state.isDirty = true;
        this.styleManager.toggleClass(CSS_CLASSES.HIGH_CONTRAST, this.state.altoContraste);
        this.saveState();
    }

    toggleImages() {
        this.state.ocultarImagenes = !this.state.ocultarImagenes;
        this.state.isDirty = true;
        this.styleManager.toggleClass(CSS_CLASSES.HIDE_IMAGES, this.state.ocultarImagenes);
        this.saveState();
    }

    toggleAnimations() {
        this.state.pausarAnimaciones = !this.state.pausarAnimaciones;
        this.state.isDirty = true;
        this.styleManager.toggleClass(CSS_CLASSES.PAUSE_ANIMATIONS, this.state.pausarAnimaciones);
        this.saveState();
    }

    //Fuente para dislexia
    toggleDyslexiaFont = (): void => {
        this.state.dislexia = !this.state.dislexia;
        this.state.isDirty = true;
        this.styleManager.toggleClass(CSS_CLASSES.DYSLEXIA_FONT, this.state.dislexia);
        this.saveState();
    };

    //Cursor grande
    toggleLargeCursor = (): void => {
        this.state.granCursor = !this.state.granCursor;
        this.state.isDirty = true;
        this.styleManager.toggleClass(CSS_CLASSES.LARGE_CURSOR, this.state.granCursor);
        this.saveState();
    };

    //Máscara de lectura
    toggleReadingMask = (): void => {
        this.state.mascaraLectura = !this.state.mascaraLectura;
        this.state.isDirty = true;
        this.styleManager.toggleClass(CSS_CLASSES.READING_MASK, this.state.mascaraLectura);

        if (this.state.mascaraLectura) {
            window.addEventListener('mousemove', this.handleMouseMove);
        } else {
            window.removeEventListener('mousemove', this.handleMouseMove);
        }
        this.saveState();
    };

    //Guía de lectura
    toggleReadingGuide = (): void => {
        this.state.guiaLectura = !this.state.guiaLectura;

        if (this.state.guiaLectura) {
            this.createReadingGuide();
            window.addEventListener('mousemove', this.handleReadingGuideMove);
        } else {
            window.removeEventListener('mousemove', this.handleReadingGuideMove);
            this.removeReadingGuide();
        }

        this.saveState();
    };

    //Lenguaje de señas
    toggleSignLanguage(): void {
        this.state.lenguajeSenas = !this.state.lenguajeSenas;
        this.signLanguageSubject.next(this.state.lenguajeSenas);
        this.saveState();
    }

    //Centro de relevo
    openCentroDeRelevo = (): void => {
        window.open('https://centroderelevo.gov.co/632/w3-channel.html', '_blank', 'noopener,noreferrer');
    };

    isSignLanguageEnabled(): boolean {
        return this.state.lenguajeSenas;
    }
}
